import React from 'react';
import { Grid, Button, Typography, IconButton, Tooltip, Paper, Chip } from '@mui/material';
import { FormatDate } from '../Functions';
import { FiberManualRecord, CheckCircleOutlineRounded, PauseCircleOutlineRounded } from '@mui/icons-material';
import { OpenInNewRounded, LinkOffRounded, InfoRounded, HighlightOffRounded } from '@mui/icons-material';
import { isFunction, isEmpty, isArray, truncate } from 'lodash';
import { blue, green, grey, red, amber } from '@mui/material/colors';
import { FormIcon, ProcessIcon } from '../Icons';
import { Link } from 'react-router-dom';
import { HtmlTooltip, UserChip } from '../Components';

const maxRefValueLength = 40;

export default function LinkItem({ data, entryId, onDelete, sx, ...props }) {
  if (!data) return null;

  return (
    <Grid container sx={{ py: 0.5, ...sx }} {...props}>
      <Grid container sx={{ p: 1 }} component={Paper} variant="outlined">
        {data.from !== entryId && (
          <>
            {data.fromType === 'form-entry' && data.fromFormEntryData && (
              <FormLinkItem linkData={data} entryData={data.fromFormEntryData} onDelete={onDelete} direction={'From'} />
            )}

            {data.fromType === 'process-entry' && data.fromProcessEntryData && (
              <ProcessLinkItem
                linkData={data}
                entryData={data.fromProcessEntryData}
                onDelete={onDelete}
                direction={'From'}
              />
            )}
          </>
        )}

        {data.to !== entryId && (
          <>
            {data.toType === 'form-entry' && data.toFormEntryData && (
              <FormLinkItem linkData={data} entryData={data.toFormEntryData} onDelete={onDelete} direction={'To'} />
            )}

            {data.toType === 'process-entry' && data.toProcessEntryData && (
              <ProcessLinkItem
                linkData={data}
                entryData={data.toProcessEntryData}
                onDelete={onDelete}
                direction={'To'}
              />
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
}

function FormLinkItem({ linkData, entryData, direction, onDelete }) {
  function openFormEntry(entryId) {
    window.open(`/forms/entry/${entryId}`, '_blank');
  }

  return (
    <Grid container alignItems={'center'}>
      <Grid container alignItems="center" wrap="nowrap">
        <FormIcon />

        {entryData.cancelledAt ? (
          <Tooltip title="Cancelled">
            <HighlightOffRounded sx={{ color: red[500], ml: 2 }} />
          </Tooltip>
        ) : entryData.completedAt ? (
          <Tooltip title="Completed">
            <CheckCircleOutlineRounded sx={{ color: green[500], ml: 2 }} />
          </Tooltip>
        ) : (
          <Tooltip title="In progress">
            <FiberManualRecord sx={{ color: blue[500], ml: 2 }} />
          </Tooltip>
        )}

        <Link to={`/forms/entry/${entryData._id}`}>
          <Typography sx={{ mx: 2, color: blue[600], fontSize: 18 }} noWrap>
            <b>{direction}:</b> #{entryData.number} - {entryData.title}
          </Typography>
        </Link>

        <Typography sx={{ ml: 'auto', color: (theme) => theme.palette.text.secondary, fontSize: '0.9em' }}>
          {entryData.completedAt
            ? `Completed ${FormatDate(entryData.completedAt)}`
            : `Started ${FormatDate(entryData.createdAt)}`}
        </Typography>

        <Button
          size="small"
          onClick={() => openFormEntry(entryData._id)}
          sx={{ ml: 1 }}
          variant="outlined"
          startIcon={<OpenInNewRounded />}
        >
          open in new tab
        </Button>

        <HtmlTooltip
          title={
            <Grid container sx={{ width: 450 }}>
              <Grid container alignItems={'center'}>
                <Typography sx={{ mr: 1 }}>This link was created {FormatDate(linkData.createdAt)} by </Typography>{' '}
                <UserChip size="small" id={linkData.createdBy} />
              </Grid>
              <Grid container sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: '0.85rem', color: (theme) => theme.palette.text.secondary }}>
                  Unique link ID: {linkData._id}
                </Typography>
              </Grid>
            </Grid>
          }
          placement="left"
        >
          <InfoRounded sx={{ color: grey[500], ml: 1 }} />
        </HtmlTooltip>

        {isFunction(onDelete) && (
          <Tooltip title="Delete link">
            <IconButton sx={{ ml: 1 }} onClick={onDelete}>
              <LinkOffRounded color="error" />
            </IconButton>
          </Tooltip>
        )}
      </Grid>

      {!isEmpty(entryData.refFieldsValues) && isArray(entryData.refFieldsValues) && (
        <Grid container alignItems="center" sx={{ mt: 1 }}>
          {entryData.refFieldsValues.map((item, i) => (
            <Tooltip title={item.title} key={i}>
              <Chip size="small" label={truncate(item.text, { length: maxRefValueLength })} sx={{ m: 0.5 }} />
            </Tooltip>
          ))}
        </Grid>
      )}
    </Grid>
  );
}

function ProcessLinkItem({ linkData, entryData, direction, onDelete }) {
  function openProcessEntry(entryId) {
    window.open(`/processes/ongoing/${entryId}`, '_blank');
  }

  return (
    <Grid container alignItems={'center'}>
      <Grid container alignItems="center" wrap="nowrap">
        <ProcessIcon />

        {entryData.cancelledAt ? (
          <Tooltip title="Cancelled">
            <HighlightOffRounded sx={{ color: red[500], ml: 2 }} />
          </Tooltip>
        ) : entryData.completedAt ? (
          <Tooltip title="Completed">
            <CheckCircleOutlineRounded sx={{ color: green[500], ml: 2 }} />
          </Tooltip>
        ) : entryData.heldAt ? (
          <Tooltip title="Held">
            <PauseCircleOutlineRounded sx={{ color: amber[600], ml: 2 }} />
          </Tooltip>
        ) : (
          <Tooltip title="In progress">
            <FiberManualRecord sx={{ color: blue[500], ml: 2 }} />
          </Tooltip>
        )}
        <Link to={`/processes/ongoing/${entryData._id}`}>
          <Typography sx={{ mx: 2, color: blue[600], fontSize: 18 }} noWrap>
            <b>{direction}:</b> #{entryData.number} - {entryData.title}
          </Typography>
        </Link>

        <Typography sx={{ ml: 'auto', color: (theme) => theme.palette.text.secondary, fontSize: '0.9em' }}>
          {entryData.completedAt
            ? `Completed ${FormatDate(entryData.completedAt)}`
            : `Started ${FormatDate(entryData.createdAt)}`}
        </Typography>

        <Button
          size="small"
          onClick={() => openProcessEntry(entryData._id)}
          sx={{ ml: 1 }}
          variant="outlined"
          startIcon={<OpenInNewRounded />}
        >
          open in new tab
        </Button>

        <HtmlTooltip
          title={
            <Grid container sx={{ width: 450 }}>
              <Grid container alignItems={'center'}>
                <Typography sx={{ mr: 1 }}>This link was created {FormatDate(linkData.createdAt)} by </Typography>{' '}
                <UserChip size="small" id={linkData.createdBy} />
              </Grid>
              <Grid container sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: '0.85rem', color: (theme) => theme.palette.text.secondary }}>
                  Unique link ID: {linkData._id}
                </Typography>
              </Grid>
            </Grid>
          }
          placement="left"
        >
          <InfoRounded sx={{ color: grey[500], ml: 1 }} />
        </HtmlTooltip>

        {isFunction(onDelete) && (
          <Tooltip title="Delete link">
            <IconButton sx={{ ml: 1 }} onClick={onDelete}>
              <LinkOffRounded color="error" />
            </IconButton>
          </Tooltip>
        )}
      </Grid>
      {!isEmpty(entryData.refFieldsValues) && isArray(entryData.refFieldsValues) && (
        <Grid container alignItems="center" sx={{ mt: 1 }}>
          {entryData.refFieldsValues.map((item, i) => (
            <Tooltip title={item.title} key={i}>
              <Chip size="small" label={truncate(item.text, { length: maxRefValueLength })} sx={{ m: 0.5 }} />
            </Tooltip>
          ))}
        </Grid>
      )}
    </Grid>
  );
}
